import React, { useRef } from "react";
import * as H from "history";
import styled, { css } from "styled-components";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment-timezone";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { ITableInstance } from "src/library/components/Tables/Table";
import { Button } from "src/library/components/Button";
import {
  IProcessJob,
  useProcessJobsApi
} from "src/core/process-job/api/processJobs.api";
import { processJobsTableColumns } from "src/core/shared/components/tables";
import { CheckboxStyled } from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import { Table } from "src/core/dashboard/components/widgets";

interface ImportOrderPageTabProps {
  // orderType change to id when we move to reference orderImportSettingId as apposed to type
  orderType: string;
  history: H.History;
}

const defaultPagination: PaginatedParams<IProcessJob> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "id", "DESC" ] ]
};

const ImportOrdersPageTab: React.ReactFCWithChildren<ImportOrderPageTabProps> = ({ orderType, history }) => {
  const [ getProcessJobsResponse, getProcessJobs ] = useProcessJobsApi("GET_PROCESS_JOBS");
  const TableRef = useRef<ITableInstance<IProcessJob>>(null);
  const [ showSuccess, setShowSuccess ] = React.useState(false);
  const tableData = React.useMemo(() => getProcessJobsResponse.data?.result || [], [ getProcessJobsResponse.data ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<IProcessJob>, pagination: PaginatedParams<IProcessJob>}) => {
    // add any new filters along with default filters for this tab
    const filters = {
      ...params.filters,
      name: "import-file-order",
      orderType
    };

    if (!filters.addedAt) {
      filters.addedAt = { $gte: moment.utc().subtract(30, "days").format("YYYY-MM-DD") };
    }

    if (!filters.status && !showSuccess) {
      filters.status = { $ne: "success" };
    }

    getProcessJobs({
      pagination: params.pagination,
      filters
    });
  }, [ showSuccess, orderType ]);

  React.useEffect(() => {
    if (typeof showSuccess === "boolean" && TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ showSuccess ]);

  const tableColumns: Array<ColumnDef<IProcessJob, keyof IProcessJob>> = React.useMemo(() => {
    return processJobsTableColumns({
      hasExpand: false,
      showName: false,
      isOrderImport: true,
      actionButton: ({ row }) => (
        <Button
          newTabPath={`/admin/orders/imports/${row.original.id}`}
          $buttonType="secondary"
          onClick={() => history.push(`/admin/orders/imports/${row.original.id}`)}
        >
          View import
        </Button>
      )
    });
  }, [ history ]);

  return (
    <StyledImportOrdersPageTab>
      <div className="show-success">
        <CheckboxStyled
          checked={!!showSuccess}
          onChange={e => setShowSuccess(e.target.checked)}
        />

        <Text className="checkbox-label">
          Show successful imports
        </Text>
      </div>

      <Table
        ref={TableRef}
        columns={tableColumns}
        defaultPagination={defaultPagination}
        defaultFilters={[
          {
            id: "name",
            value: "import-file-order"
          },
          {
            id: "orderType",
            value: orderType
          }
        ]}
        tableOf="Order Imports"
        onFetchData={onFetchData}
        pageCount={getProcessJobsResponse?.data?.pages || 0}
        rowCount={getProcessJobsResponse?.data?.count || 0}
        error={getProcessJobsResponse.error}
        loading={getProcessJobsResponse.pending}
        data={tableData}
      />
    </StyledImportOrdersPageTab>
  );
};

export default ImportOrdersPageTab;

const StyledImportOrdersPageTab = styled.div`
  ${({ theme }) => css`
    .show-success {
      display: flex;
      align-items: center;

      .checkbox-label {
        margin-left: ${theme.spacing.margins.md};
      }
    }
  `}
`;
